<script lang="ts" setup>
import { computed } from 'vue'
import type { MemberPageConfig } from '../configs/types'
import { useUserStore } from '@/stores'
import { usePurchase, useSubscription } from '@/composables/payment'
import { tripartiteLoginPage } from '@/composables/useTripartiteLogin'
import { useElementClick } from '@/composables/tracks/useElementClick'
import { VIP_TYPE } from '@/constants'
import { colors } from '@/config/brands'

interface Props {
  config: MemberPageConfig
}

const props = defineProps<Props>()

const userStore = useUserStore()
const { elementClick } = useElementClick()
const { subscribeProducts } = useSubscription('member')
const { templateProducts, loading } = usePurchase('member')

const topupShow = computed(() => {
  return templateProducts.value && templateProducts.value.length > 0
})

function login() {
  elementClick('login')
  tripartiteLoginPage('Profile')
}

// 定义样式变量供 CSS v-bind 使用
const userBgUrl = `url(${props.config.resources.userBg})`
const avatarBorderColor = props.config.theme.avatarBorder
const btnPrimaryBgColor = props.config.theme.btnPrimaryBg
const vipSubscribeBgUrl = props.config.theme.vipSubscribeBg
</script>

<template>
  <div class="cover">
    <div class="px-30px pt-40px pb-27px flex items-center justify-between">
      <div class="flex items-center space-x-14px">
        <img v-if="userStore.$state.headImageUrl" class="w-15 h-15 border rounded-full avatar-border" :src="userStore.$state.thirdHeadImgUrl" alt="avatar">
        <img v-else class="w-15 h-15 rounded-full" :src="config.resources.userDefaultAvatar" alt="avatar">
        <div>
          <p
            class="text-18px overflow-hidden whitespace-nowrap text-ellipsis text-n-text-inverse"
            :class="{ 'w-200px': userStore.$state.email }"
            :style="{ color: colors.text.inverse }"
          >
            {{ userStore.$state.thirdNickName || userStore.$state.email || userStore.$state.memberName }}
          </p>
          <p class="text-11px text-n-text-inverse">
            UID {{ userStore.$state.code }}
          </p>
          <p v-if="userStore.$state.subscribe" class="flex flex-items-center mt-10px">
            <img v-if="userStore.$state.subscribeType === VIP_TYPE.DISCOUNT" :src="config.resources.memberVipDiscount" class="w-42px h-14px">
            <img v-else :src="config.resources.memberVip" class="w-42px h-14px">
          </p>
        </div>
      </div>
      <div v-if="!userStore.$state.thirdPartType" type="link">
        <a class="px-14px py-7px text-n-primary text-13px font-bold rounded-full border btn-primary-bg border-n-text-inverse" @click="login">
          {{ config.texts.signIn }}
        </a>
      </div>
    </div>
    <div class="b-rd-2 mx-10px px-10px pb-10px bg-n-bg-card">
      <div class="h-60px px-10px flex justify-between">
        <div class="flex items-center">
          <i-assets-icon-wallet />
          <strong class="ml-12px">{{ config.texts.myWallet }}</strong>
        </div>
        <div class="flex items-center">
          <strong>{{ userStore.$state.coinBalance }}</strong>
          <span class="ml-5px text-12px text-n-text-2">{{ config.texts.coins }}</span>
          <div v-if="!loading">
            <router-link :to="topupShow ? { name: 'pay' } : { name: 'welfare' }">
              <div
                class="ml-10px px-14px !lh-8 h-8 text-13px font-bold rounded-full btn-solid text-n-text-inverse"
                @click="elementClick('TopUp')"
              >
                {{ topupShow ? config.texts.topUp : config.texts.get }}
              </div>
            </router-link>
          </div>
        </div>
      </div>

      <div v-if="subscribeProducts?.length || userStore.$state.subscribe" class="vip-cover flex items-center justify-between p-16px">
        <div>
          <div class="text-18px font-medium">
            <span v-if="!userStore.$state.subscribe" class="line-clamp-1">{{ config.texts.getVip }}</span>
            <span v-else>{{ config.texts.vipMembership }}</span>
          </div>
          <div v-if="!userStore.$state.subscribe" class="text-n-primary">
            {{ config.texts.moreCoinsMoreBenefits }}
          </div>
          <div v-if="userStore.$state.subscribeEndDate" class="text-n-primary">
            {{ config.texts.expires }} {{ $d(new Date(userStore.$state.subscribeEndDate), 'short') }}
          </div>
        </div>
        <router-link
          v-if="!userStore.$state.subscribe"
          :to="{ name: 'subscribe' }"
        >
          <div class="b-rd-14px btn-solid text-center px-2 py-1 text-12px font-medium text-n-text-inverse" @click="elementClick('Details')">
            {{ config.texts.details }}
          </div>
        </router-link>
      </div>
    </div>

    <div class="mt-10px mx-10px px-20px b-rd-2 box-shadow bg-n-bg-card">
      <div>
        <router-link
          :to="{ name: 'recharge' }"
          class="w-full h-61px flex items-center justify-between"
        >
          <div class="flex items-center space-x-7px">
            <i-assets-icon-transaction class="text-n-primary" />
            <p class="text-15px font-bold">
              {{ config.texts.transactionHistory }}
            </p>
          </div>
          <i-icon-park-outline-right class="text-n-text-3" />
        </router-link>
      </div>
      <div>
        <router-link
          :to="{ name: 'consume' }"
          class="w-full h-61px flex items-center justify-between border-t border-n-border-primary"
        >
          <div class="flex items-center space-x-7px">
            <i-assets-icon-unlocked class="text-n-primary" />
            <p class="text-15px font-bold">
              {{ config.texts.chaptersUnlocked }}
            </p>
          </div>
          <i-icon-park-outline-right class="text-n-text-3" />
        </router-link>
      </div>
      <div>
        <router-link :to="{ name: 'contact-us' }">
          <div class="w-full h-61px flex items-center justify-between border-t border-n-border-primary" @click="elementClick('contact_us')">
            <div class="flex items-center space-x-7px">
              <i-assets-icon-contact-us class="text-n-primary" />
              <p class="text-15px font-bold">
                {{ config.texts.contactUs }}
              </p>
            </div>
            <i-icon-park-outline-right class="text-n-text-3" />
          </div>
        </router-link>
      </div>
      <div>
        <router-link :to="{ name: 'setting' }">
          <div class="w-full h-61px flex items-center justify-between border-t border-n-border-primary" @click="elementClick('setting')">
            <div class="flex items-center space-x-7px">
              <i-assets-icon-setting class="text-n-primary" />
              <p class="text-15px font-bold">
                {{ config.texts.setting }}
              </p>
            </div>
            <i-icon-park-outline-right class="text-n-text-3" />
          </div>
        </router-link>
      </div>
    </div>

    <BasicTabbar tab="member" />
  </div>
</template>

<style scoped>
.cover {
  width: 100%;
  background: v-bind(userBgUrl) no-repeat top left;
  background-size: 100% auto;
}

.avatar-border {
  border-color: v-bind(avatarBorderColor);
}

.btn-primary-bg {
  background-color: v-bind(btnPrimaryBgColor);
}

.vip-cover {
  width: 100%;
  height: 80px;
  background: v-bind(vipSubscribeBgUrl) no-repeat top left;
  background-size: 100% 100%;
  border-radius: 8px;
}
</style>
